import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import {
  getBaseUrl,
  Microservice,
} from "@funded-here-interface/common/src/constant/baseUrl";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Modal, Text, Button as MButton, Group } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { COLORS } from "../../constant/colors";
import useDownloadWithdrawCSV from "../../hooks/useDownloadWithdrawCSV";
import { FileType } from "@funded-here-interface/common/src/constant/enum";
import { downloadFile } from "@funded-here-interface/common/src/Utils/file";
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MRT_SortingState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import useAllWithdrawal from "../../hooks/useAllWithdrawal";
import { formatNumbersWithThousandSeperators } from "@funded-here-interface/common/src/Utils/formatter";

// Define the Status enum
export enum Status {
  ERROR = "error",
  PENDING_APPROVAL = "pending-approval",
  APPROVED = "approved",
  PENDING_APPROVAL_BY_PSP = "pending-approval-by-psp",
  PENDING_APPROVAL2_BY_PSP = "pending-approval2-by-psp",
  COMPLETE = "complete",
  REJECTED = "rejected",
  CANCEL = "cancel",
  REJECTED_BY_SERVICE_PROVIDER = "rejected-by-psp",
}

interface Withdrawal {
  orgRoleId: string;
  id: number;
  companyName?: string;
  passportName?: string;
  amount: number;
  feesIncurred: number;
  currency: string;
  status: Status;
  createdDate: string;
  bankName: string;
  accountName: string;
  accountNumber: string;
  actionDate?: string;
  approve?: boolean;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case Status.ERROR:
      return "#d32f2f";
    case Status.PENDING_APPROVAL:
      return "#ff9800";
    case Status.APPROVED:
      return "#28a745";
    case Status.REJECTED:
    case Status.REJECTED_BY_SERVICE_PROVIDER:
      return "#dc3545";
    case Status.PENDING_APPROVAL_BY_PSP:
    case Status.PENDING_APPROVAL2_BY_PSP:
      return "#1976d2";
    case Status.COMPLETE:
      return "#4caf50";
    case Status.CANCEL:
      return "#9e9e9e";
    default:
      return "#666";
  }
};

const ProcessingWithdrawal = () => {
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [selectedWithdrawals, setSelectedWithdrawals] = useState<Withdrawal[]>(
    [],
  );
  const useDownloadWithdrawCSVMutation = useDownloadWithdrawCSV();
  const [loading, setLoading] = useState(true);
  const [isError, setError] = useState<string | null>(null);
  const [IDs, appendIDs] = useState<string[]>([]);
  const [processingIds, setProcessingIds] = useState<number[]>([]);
  const [dialogData, setDialogData] = useState<{
    id: number;
    action: "approved" | "rejected";
    displayName: string;
    amount: number;
    feesIncurred: number;
    currency: string;
    bankName: string;
    accountName: string;
    accountNumber: string;
  } | null>(null);
  const [opened, { open, close }] = useDisclosure(false);
  const [
    batchApproveDialogOpened,
    { open: openBatchApproveDialog, close: closeBatchApproveDialog },
  ] = useDisclosure(false);
  const [
    batchRejectDialogOpened,
    { open: openBatchRejectDialog, close: closeBatchRejectDialog },
  ] = useDisclosure(false);
  const isMounted = useRef(true);

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    [],
  );
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [selectedNumber, setNumsSelected] = useState(0);
  const [totalWithdrawalAmount, setTotalWithdrawalAmount] = useState<number>(0);
  const [selectedCurrency, setCurrency] = useState<string>();

  const { token } = useSelector((state: RootState) => state.auth);

  const apiColumnFilters: any = JSON.stringify(
    columnFilters
      .filter((filter) => {
        if (Array.isArray(filter.value)) {
          return filter.value.every(
            (v) => v !== null && v !== undefined && v !== "",
          );
        }
        return (
          filter.value !== null &&
          filter.value !== undefined &&
          filter.value !== ""
        );
      })
      .map((filter) => {
        let column = filter.id;
        let value = filter.value;

        return {
          [column]: value,
        };
      }),
  );

  const apiSorting: any = JSON.stringify(
    sorting.reduce(
      (acc, sort) => {
        if (sort.id && sort.desc !== undefined) {
          acc[sort.id] = sort.desc;
        }
        return acc;
      },
      {} as Record<string, any>,
    ),
  );

  const { data, isLoading, error, refetch } = useAllWithdrawal(
    token,
    pagination,
    apiColumnFilters,
    apiSorting,
  );

  const fetchWithdrawals = async () => {
    try {
      if (!token) throw new Error("No authentication token found");
      if (data) {
        if (isMounted.current) {
          setWithdrawals(data.data);
          setTotalRowCount(data.meta.totalRowCount);
          setLoading(false);
        }
      }
    } catch (err: unknown) {
      if (isMounted.current) {
        if (error) {
          setError(
            err instanceof Error ? err.message : "An unknown error occurred",
          );
          toast.error((error as Error).message);
        }
      }
    }
  };

  useEffect(() => {
    isMounted.current = true;
    fetchWithdrawals();

    return () => {
      isMounted.current = false;
    };
  }, [data, isLoading, error]);

  const handleStatusChange = (
    id: number,
    newStatus: string,
    actionDate: Date,
  ) => {
    if (!isMounted.current) return;

    const action =
      newStatus === "pending-approval-by-psp" ? "approved" : "rejected";
    // Show loading toast
    const toastId = toast.loading(`Processing...`);

    setProcessingIds((prev) => [...prev, id]);
    const data = [
      {
        id,
        status: newStatus,
        actionDate: actionDate.toISOString(),
      },
    ];

    axios
      .post(
        `${getBaseUrl(Microservice.PAYMENT)}/payment/ledger/process-withdraw`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then(() => {
        if (isMounted.current) {
          setWithdrawals((prevWithdrawals) =>
            prevWithdrawals.map((withdrawal) =>
              withdrawal.id === id
                ? {
                    ...withdrawal,
                    status: newStatus as Status,
                    actionDate: actionDate.toISOString(),
                  }
                : withdrawal,
            ),
          );

          toast.update(toastId, {
            render: `Withdrawal request(s) ${action} successfully`,
            type: "success",
            isLoading: false,
            autoClose: 3000,
          });
          refetch();
        }
      })
      .catch(() => {
        if (isMounted.current) {
          toast.error("Failed to update status");
          refetch();
        }
      })
      .finally(() => {
        if (isMounted.current) {
          setProcessingIds((prev) => prev.filter((procId) => procId !== id));
        }
      });
  };

  const handleBatchApprove = async () => {
    if (!isMounted.current || Object.keys(selectedWithdrawals).length === 0)
      return;

    closeBatchApproveDialog();

    const withdrawalArray = Object.values(selectedWithdrawals);
    const actionDate = new Date();

    // Show initial loading toast
    const toastId = toast.loading(`Processing approval...`);

    // Set all IDs as processing
    setProcessingIds((prev) => [...prev, ...withdrawalArray.map((w) => w.id)]);

    try {
      // Prepare all withdrawal data
      const allWithdrawalData = withdrawalArray.map((withdrawal) => ({
        id: withdrawal.id,
        status: Status.PENDING_APPROVAL_BY_PSP,
        actionDate: actionDate.toISOString(),
      }));

      // Process all withdrawals in parallel
      await Promise.allSettled(
        allWithdrawalData.map((data) =>
          axios.post(
            `${getBaseUrl(Microservice.PAYMENT)}/payment/ledger/process-withdraw`,
            [data],
            {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 0,
            },
          ),
        ),
      );

      // Success - all requests processed
      if (isMounted.current) {
        // Update UI for all withdrawals
        setWithdrawals((prevWithdrawals) =>
          prevWithdrawals.map((withdrawal) =>
            withdrawalArray.some((w) => w.id === withdrawal.id)
              ? {
                  ...withdrawal,
                  status: Status.PENDING_APPROVAL_BY_PSP,
                  actionDate: actionDate.toISOString(),
                }
              : withdrawal,
          ),
        );

        toast.update(toastId, {
          render: `Withdrawal request(s) approved successfully`,
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
        setSelectedWithdrawals([]);
        setRowSelection({});
        refetch();
      }
    } catch (err: any) {
      if (isMounted.current) {
        toast.update(toastId, {
          render: `Failed to approve withdrawals: ${err.message}`,
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
        refetch();
      }
    } finally {
      if (isMounted.current) {
        setProcessingIds((prev) =>
          prev.filter((id) => !withdrawalArray.some((w) => w.id === id)),
        );
      }
    }
  };

  const handleBatchReject = async () => {
    if (!isMounted.current || Object.keys(selectedWithdrawals).length === 0)
      return;

    closeBatchRejectDialog();

    const withdrawalArray = Object.values(selectedWithdrawals);
    const actionDate = new Date();

    // Show initial loading toast
    const toastId = toast.loading(`Processing rejection...`);

    // Set all IDs as processing
    setProcessingIds((prev) => [...prev, ...withdrawalArray.map((w) => w.id)]);

    try {
      // Prepare all withdrawal data
      const allWithdrawalData = withdrawalArray.map((withdrawal) => ({
        id: withdrawal.id,
        status: Status.REJECTED,
        actionDate: actionDate.toISOString(),
      }));

      // Process all withdrawals in parallel
      await Promise.allSettled(
        allWithdrawalData.map((data) =>
          axios.post(
            `${getBaseUrl(Microservice.PAYMENT)}/payment/ledger/process-withdraw`,
            [data],
            {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 0,
            },
          ),
        ),
      );

      // Success - all requests processed
      if (isMounted.current) {
        // Update UI for all withdrawals
        setWithdrawals((prevWithdrawals) =>
          prevWithdrawals.map((withdrawal) =>
            withdrawalArray.some((w) => w.id === withdrawal.id)
              ? {
                  ...withdrawal,
                  status: Status.REJECTED,
                  actionDate: actionDate.toISOString(),
                }
              : withdrawal,
          ),
        );

        toast.update(toastId, {
          render: `Withdrawal request(s) rejected successfully`,
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
        setSelectedWithdrawals([]);
        setRowSelection({});
        refetch();
      }
    } catch (err: any) {
      if (isMounted.current) {
        toast.update(toastId, {
          render: `Failed to reject withdrawals: ${err.message}`,
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
        refetch();
      }
    } finally {
      if (isMounted.current) {
        setProcessingIds((prev) =>
          prev.filter((id) => !withdrawalArray.some((w) => w.id === id)),
        );
      }
    }
  };

  const handleExportData = async () => {
    const currentDate = new Date()
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .replace(/\//g, "/");
    const fileName = `Withdrawal Request - ${currentDate}`;

    useDownloadWithdrawCSVMutation.mutate(
      { token, columnFilter: apiColumnFilters, sorting: apiSorting },
      {
        onSuccess: (data: Blob) => {
          downloadFile(data, fileName, FileType.CSV);
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      },
    );
  };

  const getDisplayName = (withdrawal: Withdrawal): string => {
    if (withdrawal.companyName && withdrawal.companyName !== null) {
      return withdrawal.companyName;
    }
    if (withdrawal.passportName) {
      return withdrawal.passportName;
    }
    return "Unknown";
  };

  const openDialog = (
    withdrawal: Withdrawal,
    action: "approved" | "rejected",
  ) => {
    setDialogData({
      id: withdrawal.id,
      action,
      displayName: getDisplayName(withdrawal),
      amount: withdrawal.amount,
      feesIncurred: withdrawal.feesIncurred,
      currency: withdrawal.currency,
      bankName: withdrawal.bankName,
      accountName: withdrawal.accountName,
      accountNumber: withdrawal.accountNumber,
    });
    open();
  };

  const closeDialog = () => {
    close();
    setDialogData(null);
  };

  const confirmAction = () => {
    if (dialogData) {
      const actionDate = new Date();
      handleStatusChange(
        dialogData.id,
        dialogData.action === "approved"
          ? Status.PENDING_APPROVAL_BY_PSP
          : dialogData.action,
        actionDate,
      );
      closeDialog();
    }
  };

  const columns = useMemo<MRT_ColumnDef<Withdrawal>[]>(
    () => [
      {
        accessorKey: "id",
        header: "Transaction ID",
        size: 150,
      },
      {
        accessorKey: "investorName",
        header: "Investor Name",
        size: 150,
        accessorFn: (row) => getDisplayName(row),
        filterFn: "includesString",
      },
      {
        accessorKey: "bankName",
        header: "Bank Name",
        size: 150,
      },
      {
        accessorKey: "accountName",
        header: "Account Name",
        size: 150,
      },
      {
        accessorKey: "accountNumber",
        header: "Account Number",
        size: 150,
      },
      {
        accessorKey: "amount",
        header: "Withdrawal Amount",
        size: 120,
        Cell: ({ cell }) =>
          formatNumbersWithThousandSeperators(cell.getValue<number>()),
      },
      {
        accessorKey: "feesIncurred",
        header: "Transaction Fee",
        size: 100,
        Cell: ({ cell }) =>
          formatNumbersWithThousandSeperators(cell.getValue<number>()),
      },
      {
        accessorKey: "netAmount",
        header: "Net Amount",
        size: 120,
        Cell: ({ cell }) =>
          formatNumbersWithThousandSeperators(Number(cell.getValue())),
      },
      {
        accessorKey: "currency",
        header: "Currency",
        size: 100,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        Cell: ({ cell }) => (
          <Text
            style={{
              color: getStatusColor(cell.getValue<string>()),
              fontWeight:
                cell.getValue<string>() === Status.ERROR ? "bold" : "normal",
            }}
          >
            {cell.getValue<string>()}
          </Text>
        ),
      },
      {
        accessorKey: "createdDate",
        header: "Withdrawal Request Date",
        size: 180,
        enableColumnFilter: false,
        Cell: ({ cell }) =>
          new Date(cell.getValue<string>())
            .toLocaleString("en-GB", {
              timeZone: "Asia/Singapore",
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
            .replace(",", "") || "Invalid Date",
        sortingFn: "datetime",
      },
      {
        accessorKey: "action",
        header: "Action",
        size: 200,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const withdrawal = row.original;
          return withdrawal.status === Status.PENDING_APPROVAL ? (
            <>
              <button
                style={approveButtonStyle}
                disabled={processingIds.includes(withdrawal.id)}
                onClick={() => openDialog(withdrawal, "approved")}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#28a745")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#fff")
                }
              >
                Approve
              </button>
              <button
                style={rejectButtonStyle}
                disabled={processingIds.includes(withdrawal.id)}
                onClick={() => openDialog(withdrawal, "rejected")}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#dc3545")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#fff")
                }
              >
                Reject
              </button>
            </>
          ) : withdrawal.status === Status.APPROVED ||
            withdrawal.status === Status.PENDING_APPROVAL_BY_PSP ||
            withdrawal.status === Status.PENDING_APPROVAL2_BY_PSP ||
            withdrawal.status === Status.REJECTED ? (
            withdrawal.actionDate ? (
              new Date(withdrawal.actionDate)
                .toLocaleString("en-GB", {
                  timeZone: "Asia/Singapore",
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })
                .replace(",", "")
            ) : (
              "N/A"
            )
          ) : withdrawal.status === Status.ERROR ? (
            <span style={{ color: "#d32f2f", fontWeight: "bold" }}>
              Error Occurred
            </span>
          ) : (
            withdrawal.status
          );
        },
      },
    ],
    [],
  );

  const table = useMantineReactTable({
    enableRowSelection: (row) => {
      return row.original.status === Status.PENDING_APPROVAL;
    },
    enableColumnActions: false,
    enableColumnFilters: true,
    paginationDisplayMode: "pages",
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    state: { pagination, rowSelection, isLoading, columnFilters, sorting },
    rowCount: totalRowCount,
    positionToolbarAlertBanner: "none",
    getRowId: (originalRow) => originalRow.id?.toString() ?? "",
    columns,
    data: data?.data ?? [],
    initialState: { density: "xs" },

    mantineTableProps: {
      withColumnBorders: true,
      withBorder: true,
    },
    mantineTableHeadCellProps: {
      style: {
        padding: "0.5rem 1.0rem",
        color: "#23262a",
      },
    },
    mantineTableBodyCellProps: {
      style: {
        padding: "0.5rem",
      },
    },
  });

  useEffect(() => {
    const keys = Object.keys(rowSelection);
    setNumsSelected(keys.length);
    appendIDs(keys);
    const selectedIds = keys.map(Number);
    const selectedData =
      data?.data.filter((item) => selectedIds.includes(item.id)) ?? [];
    setSelectedWithdrawals((prev) => {
      const updated = { ...prev };
      data?.data.forEach((item) => {
        if (!selectedIds.includes(item.id)) {
          delete updated[item.id];
        }
      });

      selectedData.forEach((item) => {
        updated[item.id] = item;
      });

      return updated;
    });
  }, [rowSelection, data]);

  useEffect(() => {
    const values = Object.values(selectedWithdrawals);

    const total = values.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0,
    );

    const currency = values[0]?.currency;
    setCurrency(currency);
    setTotalWithdrawalAmount(total);
  }, [selectedWithdrawals]);

  const baseButtonStyle: React.CSSProperties = {
    width: "80px",
    padding: "5px 10px",
    backgroundColor: "#fff",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "background-color 0.3s",
    textAlign: "center",
    fontSize: "14px",
  };

  const approveButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    border: "1px solid #28a745",
    marginRight: "5px",
  };

  const rejectButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    border: "1px solid #dc3545",
  };

  const scrollListStyle: React.CSSProperties = {
    maxHeight: "300px",
    overflowY: "auto",
    marginBottom: "20px",
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
  };

  const exportButtonStyle: React.CSSProperties = {
    padding: "10px 20px",
    fontSize: "16px",
    fontWeight: 600,
    backgroundColor: "#000000",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "background-color 0.3s ease",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  };

  if (loading)
    return (
      <div style={{ textAlign: "center", padding: "20px", fontSize: "18px" }}>
        Loading...
      </div>
    );
  if (error)
    return (
      <div
        style={{
          textAlign: "center",
          padding: "20px",
          color: "#d32f2f",
          fontSize: "18px",
        }}
      >
        Error: {isError}
      </div>
    );

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "20px",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <h1
        style={{
          fontWeight: 600,
          color: "#333",
          marginBottom: "20px",
          fontSize: "28px",
        }}
      >
        Investor Withdrawal
      </h1>
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
        <button
          style={exportButtonStyle}
          onClick={handleExportData}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "rgb(67, 67, 67)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "#000000")
          }
        >
          Export CSV
        </button>
        <button
          style={{
            ...exportButtonStyle,
            backgroundColor: IDs.length === 0 ? "#ccc" : "#2A7D71",
            cursor: IDs.length === 0 ? "not-allowed" : "pointer",
          }}
          onClick={openBatchApproveDialog}
          disabled={IDs.length === 0}
          onMouseEnter={(e) => {
            if (IDs.length > 0)
              e.currentTarget.style.backgroundColor = "rgb(29, 92, 82)";
          }}
          onMouseLeave={(e) => {
            if (IDs.length > 0)
              e.currentTarget.style.backgroundColor = "#2A7D71";
          }}
        >
          Approve
        </button>
        <button
          style={{
            ...exportButtonStyle,
            backgroundColor: IDs.length === 0 ? "#ccc" : "#dc3545",
            cursor: IDs.length === 0 ? "not-allowed" : "pointer",
          }}
          onClick={openBatchRejectDialog}
          disabled={IDs.length === 0}
          onMouseEnter={(e) => {
            if (IDs.length > 0)
              e.currentTarget.style.backgroundColor = "#c82333";
          }}
          onMouseLeave={(e) => {
            if (IDs.length > 0)
              e.currentTarget.style.backgroundColor = "#dc3545";
          }}
        >
          Reject
        </button>
      </div>
      <MantineReactTable table={table} />

      <Modal
        opened={opened}
        onClose={closeDialog}
        size="lg"
        centered
        overlayProps={{ blur: 3, color: "rgba(0, 0, 0, 0.5)" }}
        withCloseButton
        radius="md"
        padding="xl"
        styles={{
          content: {
            backgroundColor: "#FFFFFF",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            borderRadius: "12px",
          },
          header: {
            backgroundColor: "#F5F7FA",
            borderBottom: "1px solid #E0E0E0",
            padding: "16px 24px",
            borderRadius: "12px 12px 0 0",
          },
          body: { padding: "24px" },
        }}
      >
        {dialogData && (
          <>
            <Text
              ta="center"
              style={{
                color: COLORS.agreementFont,
                fontWeight: 700,
                fontSize: "24px",
                marginBottom: "16px",
              }}
            >
              Withdrawal{" "}
              {dialogData.action === "approved" ? "Approval" : "Rejection"}{" "}
              Confirmation
            </Text>
            <div
              style={{
                backgroundColor: "#F9F9F9",
                borderRadius: "8px",
                padding: "20px",
                marginBottom: "20px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
              }}
            >
              <Text size="lg" weight={600} mb="md" style={{ color: "#333" }}>
                Are you sure you want to{" "}
                {dialogData.action === "approved" ? "approve" : "reject"} the
                following withdrawal request?
              </Text>
              <div style={{ marginBottom: "12px" }}>
                <Text size="sm" style={{ color: "#666", fontWeight: 500 }}>
                  Investor Name: {dialogData.displayName}
                </Text>
                <Text size="sm" style={{ color: "#666", fontWeight: 500 }}>
                  Bank Name: {dialogData.bankName}
                </Text>
                <Text size="sm" style={{ color: "#666", fontWeight: 500 }}>
                  Account Name: {dialogData.accountName}
                </Text>
                <Text size="sm" style={{ color: "#666", fontWeight: 500 }}>
                  Account Number: {dialogData.accountNumber}
                </Text>
                <Text size="sm" style={{ color: "#666", fontWeight: 500 }}>
                  Withdrawal Amount: {dialogData.currency}{" "}
                  {formatNumbersWithThousandSeperators(dialogData.amount, true)}
                </Text>
                <Text size="sm" style={{ color: "#666", fontWeight: 500 }}>
                  Transaction Fee: {dialogData.currency}{" "}
                  {formatNumbersWithThousandSeperators(
                    dialogData.feesIncurred,
                    true,
                  )}
                </Text>
              </div>
            </div>
            <Group mt="xl" position="center" spacing="xl">
              <MButton
                onClick={closeDialog}
                color="gray"
                variant="outline"
                radius="md"
                size="md"
                disabled={!token}
                styles={{
                  root: {
                    borderColor: "#757575",
                    color: "#757575",
                    "&:hover": {
                      backgroundColor: "rgba(117, 117, 117, 0.1)",
                      borderColor: "#666",
                    },
                  },
                }}
              >
                No, Cancel
              </MButton>
              <MButton
                onClick={confirmAction}
                color={dialogData.action === "approved" ? "blue" : "red"}
                variant="filled"
                radius="md"
                size="md"
                disabled={!token}
                styles={{
                  root: {
                    backgroundColor:
                      dialogData.action === "approved"
                        ? COLORS.agreementFont
                        : "#dc3545",
                    "&:hover": {
                      backgroundColor:
                        dialogData.action === "approved"
                          ? "#2A7D71"
                          : "#c82333",
                    },
                  },
                }}
              >
                Yes, Confirm{" "}
                {dialogData.action === "approved" ? "Approval" : "Rejection"}
              </MButton>
            </Group>
          </>
        )}
      </Modal>

      <Modal
        opened={batchApproveDialogOpened}
        onClose={closeBatchApproveDialog}
        size="lg"
        centered
        overlayProps={{ blur: 3, color: "rgba(0, 0, 0, 0.5)" }}
        withCloseButton
        radius="md"
        padding="xl"
        styles={{
          content: {
            backgroundColor: "#FFFFFF",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            borderRadius: "12px",
          },
          header: {
            backgroundColor: "#F5F7FA",
            borderBottom: "1px solid #E0E0E0",
            padding: "16px 24px",
            borderRadius: "12px 12px 0 0",
          },
          body: { padding: "24px" },
        }}
      >
        <Text
          ta="center"
          style={{
            color: COLORS.agreementFont,
            fontWeight: 700,
            fontSize: "24px",
            marginBottom: "16px",
          }}
        >
          Withdrawal Approval Confirmation
        </Text>
        <Text size="lg" weight={600} mb="md" style={{ color: "#333" }}>
          Are you sure you want to approve the following withdrawal request(s)?
        </Text>
        <ol style={scrollListStyle}>
          {Object.values(selectedWithdrawals).map((record) => (
            <li
              key={record.id}
              style={{
                color: "#666",
                fontSize: "16px",
                listStyleType: "decimal",
                padding: "4px 0",
              }}
            >
              {getDisplayName(record).replace(/^\d+\.\s*/, "")}
            </li>
          ))}
        </ol>
        <Text size="sm" style={{ color: "#666", fontWeight: 500 }}>
          Total Withdrawal Amount: {selectedCurrency || ""}{" "}
          {totalWithdrawalAmount.toLocaleString("en-GB", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Text>
        <Text
          size="sm"
          style={{ color: "#666", fontWeight: 500, marginTop: "8px" }}
        >
          Number of Withdrawal Request Selected: {selectedNumber}
        </Text>
        <Group mt="xl" position="center" spacing="xl">
          <MButton
            onClick={closeBatchApproveDialog}
            color="gray"
            variant="outline"
            radius="md"
            size="md"
            disabled={!token}
            styles={{
              root: {
                borderColor: "#757575",
                color: "#757575",
                "&:hover": {
                  backgroundColor: "rgba(117, 117, 117, 0.1)",
                  borderColor: "#666",
                },
              },
            }}
          >
            No, Cancel
          </MButton>
          <MButton
            onClick={handleBatchApprove}
            color="blue"
            variant="filled"
            radius="md"
            size="md"
            disabled={!token}
            styles={{
              root: {
                backgroundColor: COLORS.agreementFont,
                "&:hover": { backgroundColor: "#2A7D71" },
              },
            }}
          >
            Yes, Confirm Approval
          </MButton>
        </Group>
      </Modal>

      <Modal
        opened={batchRejectDialogOpened}
        onClose={closeBatchRejectDialog}
        size="lg"
        centered
        overlayProps={{ blur: 3, color: "rgba(0, 0, 0, 0.5)" }}
        withCloseButton
        radius="md"
        padding="xl"
        styles={{
          content: {
            backgroundColor: "#FFFFFF",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            borderRadius: "12px",
          },
          header: {
            backgroundColor: "#F5F7FA",
            borderBottom: "1px solid #E0E0E0",
            padding: "16px 24px",
            borderRadius: "12px 12px 0 0",
          },
          body: { padding: "24px" },
        }}
      >
        <Text
          ta="center"
          style={{
            color: COLORS.agreementFont,
            fontWeight: 700,
            fontSize: "24px",
            marginBottom: "16px",
          }}
        >
          Withdrawal Rejection Confirmation
        </Text>
        <Text size="lg" weight={600} mb="md" style={{ color: "#333" }}>
          Are you sure you want to reject the following withdrawal request(s)?
        </Text>
        <ol style={scrollListStyle}>
          {Object.values(selectedWithdrawals).map((record) => (
            <li
              key={record.id}
              style={{
                color: "#666",
                fontSize: "16px",
                listStyleType: "decimal",
                padding: "4px 0",
              }}
            >
              {getDisplayName(record).replace(/^\d+\.\s*/, "")}
            </li>
          ))}
        </ol>
        <Text size="sm" style={{ color: "#666", fontWeight: 500 }}>
          Total Withdrawal Amount: {selectedCurrency || ""}{" "}
          {totalWithdrawalAmount.toLocaleString("en-GB", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Text>
        <Text
          size="sm"
          style={{ color: "#666", fontWeight: 500, marginTop: "8px" }}
        >
          Number of Withdrawal Request Selected: {IDs.length}
        </Text>
        <Group mt="xl" position="center" spacing="xl">
          <MButton
            onClick={closeBatchRejectDialog}
            color="gray"
            variant="outline"
            radius="md"
            size="md"
            disabled={!token}
            styles={{
              root: {
                borderColor: "#757575",
                color: "#757575",
                "&:hover": {
                  backgroundColor: "rgba(117, 117, 117, 0.1)",
                  borderColor: "#666",
                },
              },
            }}
          >
            No, Cancel
          </MButton>
          <MButton
            onClick={handleBatchReject}
            color="red"
            variant="filled"
            radius="md"
            size="md"
            disabled={!token}
            styles={{
              root: {
                backgroundColor: "#dc3545",
                "&:hover": { backgroundColor: "#c82333" },
              },
            }}
          >
            Yes, Confirm Rejection
          </MButton>
        </Group>
      </Modal>

      <ToastContainer />
    </div>
  );
};

export default ProcessingWithdrawal;
